import { PdsWidget } from './pds-widget';
import { NavItem } from './interfaces/nav-item';

export class PdsGlobalCreateMenu extends PdsWidget {
  is = 'pds-global-create-menu';

  beforeNavigateFunction?: (event: any) => void;
  clickedOutsideDropDown: (event: any) => void;
  defaultMenuItem?: NavItem;
  isGlobalCreateMenuInSetupMode = false;
  isGlobalCreateMenuShowing = false;
  menuItems: Array<NavItem>;

  ready(): void {
    super.ready();
    this.isGlobalCreateMenuInSetupMode = false;
    this.isGlobalCreateMenuShowing = false;
  }

  attached(): void {
    super.attached();
    this.clickedOutsideDropDown = (event: Event): void => {
      // If the Dropdown menu is showing...
      if (this.isGlobalCreateMenuShowing) {
        let dropdown = <HTMLElement>this.querySelector('.pds-global-create-menu-drop-down');
        let menuIcon = <HTMLElement>this.querySelector('#global-create-menu-link');
        // ...and the click came from outside the dropdown
        if (dropdown != null && !dropdown.contains(<HTMLElement>event.target) && !menuIcon.contains(<HTMLElement>event.target)) {
          // then hide the dropdown!
          this.hideGlobalCreateMenu();
        }
      }
    };
  }

  // Aria label
  // By having each of these explicitly enumerating these arguements rather than relying on "this.property", polymer change detection will work for the template
  // It also makes them easy to unit test
  getAriaLabelForMenuItem(redrawTrigger: any, isGlobalCreateMenuInSetupMode: boolean, menuItem: NavItem, menuHeader?: NavItem | undefined): string {
    // I made this forgiving if we can't find a menu header for default menu item
    let menuHeaderName = menuHeader ? menuHeader.name : '';
    if (isGlobalCreateMenuInSetupMode) {
      return this.translate(redrawTrigger, 'pds|global create menu widget|Set xx xx to default action', menuHeaderName, menuItem.name);
    } else {
      return this.translate(redrawTrigger, 'pds|global create menu widget|Navigate to xx xx', menuHeaderName, menuItem.name);
    }
  }

  getAriaLabelForPlusButton(redrawTrigger: any, isGlobalCreateMenuShowing: boolean, menuItems: NavItem[], defaultMenuItem?: NavItem | undefined): string {
    if (defaultMenuItem == null) {
      // There is no default, plus button will open in setup mode
      return this.translate(redrawTrigger, 'pds|global create menu widget|View global create menu default action setup');
      // Technically, if you're already in setup mode you'll stay in setup mode... but having a message for that seems absurd
    } else {
      // Menu is not open and there is a default, plus button will navigate to the menu item
      let defaultmenuHeader: NavItem | undefined;
      // We have to find the header since it's not stored
      if (defaultMenuItem.context != null) {
        for (let menuHeader of menuItems) {
          if (menuHeader.children != null) {
            for (let menuItem of menuHeader.children) {
              if (menuItem.context != null && menuItem.context.uid === defaultMenuItem.context.uid) {
                defaultmenuHeader = menuHeader;
              }
            }
          }
        }
      }
      // Hardcoded false for isGlobalCreateMenuInSetupMode because the plus button's function doesn't change in setup mode
      return this.getAriaLabelForMenuItem(redrawTrigger, false, defaultMenuItem, defaultmenuHeader);
    }
  }

  getAriaLabelForCaretButton(redrawTrigger: any, isGlobalCreateMenuShowing: boolean, defaultMenuItem?: NavItem | undefined): string {
    if (isGlobalCreateMenuShowing) {
      // Menu is open, the caret button will close it
      return this.translate(redrawTrigger, 'pds|global create menu widget|Close global create menu');
    } else {
      if (defaultMenuItem == null) {
        // Menu is not open and there is no default, caret button will open in setup mode
        return this.translate(redrawTrigger, 'pds|global create menu widget|View global create menu default action setup');
      } else {
        // Menu is not open and there is a default, caret button will open it normally
        return this.translate(redrawTrigger, 'pds|global create menu widget|Open global create menu');
      }
    }
  }

  // Display
  getIconName(menuItem: NavItem): string {
    if (menuItem.iconName == null || menuItem.iconName  === '') {
      return 'generic-f';
    } else {
      return menuItem.iconName;
    }
  }

  isDefault(menuItem: NavItem, defaultMenuItem?: NavItem): boolean {
    if (defaultMenuItem == null) {
      return false;
    } else {
      return menuItem.id === defaultMenuItem.id;
    }
  }

  toggleGlobalCreateMenuDisplay(event: Event): void {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.isGlobalCreateMenuShowing) {
      // Hide it
      this.hideGlobalCreateMenu();
    } else {
      // Show it
      this.showGlobalCreateMenu();
    }
  }

  toggleGlobalCreateMenuSetupMode(event: Event): void {
    this.isGlobalCreateMenuInSetupMode = !this.isGlobalCreateMenuInSetupMode;
  }

  // Event
  clickMenuItem(event: any): void {
    if (this.isGlobalCreateMenuInSetupMode) {
      this.setDefault(event);
    } else {
      this.navigateToMenuItem(event);
    }
  }

  navigateToMenuItem(event: any): void {
    if (this.beforeNavigateFunction != null) {
      // The beforeNavigateFunction is used in UC to clear the currently selected assignment, but other apps may/may not have uses for it
      this.beforeNavigateFunction(event);
    }
    event.preventDefault();
    event.stopImmediatePropagation();

    let menuItem: NavItem | undefined;
    if (event && event.model) {
      // Use the one from the event
      menuItem = event.model.menuItem; //polymer
    } else {
      // Use the default
      menuItem = this.defaultMenuItem;
    }

    if (menuItem == null) {
      // Enter Default Setup mode
      if (this.isGlobalCreateMenuShowing) {
        // If the menu is open, just switch to setup mode
        this.isGlobalCreateMenuInSetupMode = true;
      } else {
        // If the menu is not open, show it(which will also turn on the setup mode)
        this.showGlobalCreateMenu();
      }
    } else if (menuItem.onUserClick) {
      // Yay, we have a menuItem with the (required) onUserClick event
      menuItem.onUserClick(menuItem, event);
      if (this.isGlobalCreateMenuShowing) {
        this.hideGlobalCreateMenu();
      }
    } else {
      console.error('menu item clicked with no click event');
    }
  }

  setDefault(event): void {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.defaultMenuItem = event.model.menuItem;
    this.fire('pdsDefaultSet', event.model.menuItem);
  }

  // Private - event listener instantiation
  private hideGlobalCreateMenu(): void {
    this.isGlobalCreateMenuShowing = false;
    window.removeEventListener('click', this.clickedOutsideDropDown);
  }

  private showGlobalCreateMenu(): void {
    this.isGlobalCreateMenuShowing = true;
    // If there is no default, show the menu in setup mode
    this.isGlobalCreateMenuInSetupMode = this.defaultMenuItem == null;
    window.addEventListener('click', this.clickedOutsideDropDown, true);
  }
}
